import * as React from 'react'
import { Graph, type GraphConfigInterface } from '@cosmos.gl/graph'
import type { GraphResponse } from '@axilon-apps/domains-ontology-ui-state'
import {
  buildLinkVisuals,
  buildPointPositions,
  buildPointVisuals,
  toCosmosElements,
  type CosmosGraphData
} from './cosmos-graph-utils'

/**
 * cosmos.gl's per-frame render controls are private on the `Graph` class, so we
 * feature-detect them at runtime and reach through this structural shape. We
 * cast via `unknown` (intersecting with `Graph` collapses to `never` because the
 * members are private) — the runtime object still carries the methods.
 */
type FrameControls = {
  startFrames?: () => void
  stopFrames?: () => void
  render?: () => void
}

const asFrameControls = (g: Graph | null): FrameControls | null =>
  g as unknown as FrameControls | null

/** Simulation coordinate space; positions we feed in live in `[0, SPACE_SIZE]`. */
const SPACE_SIZE = 8192

/** Default force tuning (full graph) — see config below for rationale. */
const DEFAULT_GRAVITY = 0.1
const DEFAULT_DECAY = 5000
const DEFAULT_REPULSION = 2.5
const DEFAULT_CLUSTER = 0.25

export interface CosmosGraphCanvasHandle {
  fit: () => void
  zoomIn: () => void
  zoomOut: () => void
  revealNode: (nodeId: string) => void
}

interface CosmosGraphCanvasProps {
  graph: GraphResponse
  selectedNodeIds: string[]
  highlightedNodeIds?: string[]
  onToggleNode: (nodeId: string | null) => void
  onClearHighlights?: () => void
  height?: string
}

/**
 * GPU-accelerated ontology graph canvas built on
 * [cosmos.gl](https://cosmos.gl) (`@cosmos.gl/graph`). Replaces the previous
 * Neo4j NVL renderer while keeping the same imperative handle
 * (`fit` / `zoomIn` / `zoomOut` / `revealNode`) and props so `GraphPage` is
 * unchanged apart from the import.
 *
 * Structural data (positions/links) is only rebuilt when the `graph` prop
 * changes; selection, hover, and highlight emphasis are pushed through the
 * cheap per-point/per-link color & size buffers so interaction never resets
 * the running force simulation.
 */
const CosmosGraphCanvas = React.forwardRef<CosmosGraphCanvasHandle, CosmosGraphCanvasProps>(
  function CosmosGraphCanvas(
    {
      graph,
      highlightedNodeIds = [],
      onToggleNode,
      onClearHighlights,
      selectedNodeIds,
      height = '640px'
    },
    ref
  ) {
    const containerRef = React.useRef<HTMLDivElement | null>(null)
    const graphRef = React.useRef<Graph | null>(null)
    const dataRef = React.useRef<CosmosGraphData | null>(null)
    const knownPositionsRef = React.useRef<Map<string, [number, number]>>(new Map())
    const [hoveredNodeId, setHoveredNodeId] = React.useState<string | null>(null)
    const [cursorPos, setCursorPos] = React.useState<{ x: number; y: number } | null>(null)

    // Mirror hovered id into a ref so the mousemove handler can cheaply gate
    // cursor-position updates to "only while a node is hovered".
    const hoveredNodeIdRef = React.useRef<string | null>(null)
    hoveredNodeIdRef.current = hoveredNodeId

    // Cluster label for the hovered node (the name of its container cluster),
    // shown as a small floating tag on hover. `null` when the node is unclustered.
    const hoverCluster = React.useMemo(() => {
      const data = dataRef.current
      if (!data || !hoveredNodeId) return null
      const index = data.idToIndex.get(hoveredNodeId)
      if (index === undefined) return null
      const cluster = data.pointClusters[index]
      if (cluster === undefined) return null
      return { name: data.clusterLabels[cluster], type: data.clusterTypes[cluster] }
    }, [hoveredNodeId])

    // Keep the latest event callbacks reachable from the cosmos config closure
    // (the Graph instance is created once on mount).
    const onToggleNodeRef = React.useRef(onToggleNode)
    onToggleNodeRef.current = onToggleNode
    const onClearHighlightsRef = React.useRef(onClearHighlights)
    onClearHighlightsRef.current = onClearHighlights

    // --- Render-loop gating -------------------------------------------------
    // cosmos.gl runs an UNCONDITIONAL requestAnimationFrame loop for the whole
    // life of the instance: its private `frame` loop "schedules itself to run
    // continuously" and, per the library docs, rendering keeps going even after
    // the force simulation ends (there is no public render-on-demand switch — a
    // static graph still redraws ~60fps forever). A CPU profile of the app while
    // the chat sidebar streamed showed this loop (`resizeCanvas` + the per-frame
    // WebGL draw) was BY FAR the dominant main-thread cost — it janked scroll and
    // typing across the entire app even when the graph was idle and the user was
    // only using the sidebar. So we drive the loop ourselves: keep rendering
    // while the pointer is over the graph, the simulation is running, or a
    // programmatic transition (fit/zoom/highlight) is in flight, and halt it once
    // the graph is idle + unhovered. cosmos preserves its method names at
    // runtime; we feature-detect the internal frame controls and, if a future
    // version renames them, fall back to the original always-on behavior.
    const renderingRef = React.useRef(true)
    const canPauseRef = React.useRef(false)
    const pointerOverRef = React.useRef(false)
    // Absolute time (ms epoch) until which the loop must keep running. Only ever
    // pushed later, never earlier, so a short repaint (e.g. a selection change)
    // can't cut off a longer settle window.
    const idleDeadlineRef = React.useRef(0)
    const pauseTimerRef = React.useRef<number | null>(null)

    const dbgBump = (key: string) => {
      if (typeof window === 'undefined') return
      const dbg = (window as unknown as { __cosmosDbg?: Record<string, number> }).__cosmosDbg
      if (dbg) dbg[key] = (dbg[key] ?? 0) + 1
    }

    const startRendering = React.useCallback(() => {
      if (!graphRef.current || renderingRef.current) return
      const g = asFrameControls(graphRef.current)
      if (!g) return
      renderingRef.current = true
      if (typeof g.startFrames === 'function') g.startFrames()
      else g.render?.()
      dbgBump('starts')
    }, [])

    // Ensure a single pending check that halts the loop once we're past the idle
    // deadline and the pointer isn't on the graph. We intentionally do NOT wait
    // for the force simulation to end: with a slow `decay` it can stay "running"
    // for tens of seconds, and freezing it (the last frame stays painted) is a
    // fine trade for not pinning the main thread. Interaction / data / visual
    // changes push the deadline out and resume rendering.
    const schedulePauseCheck = React.useCallback(() => {
      if (!canPauseRef.current || pauseTimerRef.current !== null) return
      const check = () => {
        const g = asFrameControls(graphRef.current)
        const remaining = idleDeadlineRef.current - Date.now()
        if (g && (pointerOverRef.current || remaining > 0)) {
          pauseTimerRef.current = window.setTimeout(check, remaining > 0 ? remaining : 400)
          return
        }
        pauseTimerRef.current = null
        if (!g || !renderingRef.current) return
        renderingRef.current = false
        g.stopFrames?.()
        dbgBump('stops')
      }
      pauseTimerRef.current = window.setTimeout(
        check,
        Math.max(0, idleDeadlineRef.current - Date.now())
      )
    }, [])

    // Push the idle deadline out by `delayMs` (never inward) and arm the checker.
    const armIdlePause = React.useCallback(
      (delayMs: number) => {
        idleDeadlineRef.current = Math.max(idleDeadlineRef.current, Date.now() + delayMs)
        schedulePauseCheck()
      },
      [schedulePauseCheck]
    )

    // Render now + for the duration of a transition, then return to idle.
    const keepAliveFor = React.useCallback(
      (delayMs: number) => {
        startRendering()
        armIdlePause(delayMs)
      },
      [armIdlePause, startRendering]
    )

    /** Persist the GPU's current point positions so neighbor expansion is stable. */
    const capturePositions = React.useCallback(() => {
      const g = graphRef.current
      const data = dataRef.current
      if (!g || !data) return
      const flat = g.getPointPositions()
      if (!flat || flat.length < data.pointCount * 2) return
      for (let i = 0; i < data.pointCount; i++) {
        knownPositionsRef.current.set(data.indexToId[i], [flat[i * 2], flat[i * 2 + 1]])
      }
    }, [])

    const applyVisualState = React.useCallback(() => {
      const g = graphRef.current
      const data = dataRef.current
      if (!g || !data) return
      const selectedSet = new Set(selectedNodeIds)
      const highlightedSet = new Set(highlightedNodeIds)
      const { colors, sizes } = buildPointVisuals(data, selectedSet, highlightedSet, hoveredNodeId)
      const link = buildLinkVisuals(data, selectedSet, hoveredNodeId)
      g.setPointColors(colors)
      g.setPointSizes(sizes)
      g.setLinkColors(link.colors)
      g.setLinkWidths(link.widths)
      g.render()
      // A visual push needs a repaint even if the loop was idle; wake it briefly.
      keepAliveFor(600)
    }, [selectedNodeIds, highlightedNodeIds, hoveredNodeId, keepAliveFor])

    // Create the Graph instance once.
    React.useEffect(() => {
      const container = containerRef.current
      if (!container) return

      const config: GraphConfigInterface = {
        spaceSize: SPACE_SIZE,
        backgroundColor: [0, 0, 0, 0],
        pointDefaultColor: '#94a3b8',
        pointDefaultSize: 5,
        renderHoveredPointRing: true,
        hoveredPointRingColor: '#0ea5e9',
        hoveredPointCursor: 'pointer',
        // Grow/shrink points with the zoom level so nodes enlarge as you zoom in
        // instead of staying a fixed screen size.
        scalePointsOnZoom: true,
        linkDefaultColor: '#64748b',
        linkWidthScale: 1,
        curvedLinks: false,
        enableDrag: true,
        enableZoom: true,
        fitViewOnInit: true,
        fitViewDelay: 350,
        fitViewPadding: 0.2,
        simulationFriction: 0.85,
        simulationLinkSpring: 0.6,
        // Longer links + stronger repulsion + weaker gravity spread the graph
        // out so dense hubs (a provider with thousands of tags, big UDTs) don't
        // collapse into tight balls. Spacing is kept well above the rendered node
        // radius so points don't overlap — important because `scalePointsOnZoom`
        // grows nodes with zoom, so any base-layout overlap stays visible at all
        // zoom levels instead of being relieved by zooming in.
        simulationLinkDistance: 80,
        simulationRepulsion: DEFAULT_REPULSION,
        simulationGravity: DEFAULT_GRAVITY,
        simulationDecay: DEFAULT_DECAY,
        // Pull each node toward its structural cluster center (clusters are
        // assigned per-point in cosmos-graph-utils — every container + its
        // subtree). Kept moderate so clusters stay grouped without packing tight.
        simulationCluster: DEFAULT_CLUSTER,
        onPointClick: (index) => {
          const id = dataRef.current?.indexToId[index]
          if (id) onToggleNodeRef.current(id)
          // Zoom in on whatever node was clicked (every click, not just new
          // selections), centering it in the viewport.
          graphRef.current?.zoomToPointByIndex(index, 600, 3, true)
        },
        onBackgroundClick: () => {
          onToggleNodeRef.current(null)
          onClearHighlightsRef.current?.()
        },
        onPointMouseOver: (index) => {
          setHoveredNodeId(dataRef.current?.indexToId[index] ?? null)
        },
        onPointMouseOut: () => {
          setHoveredNodeId(null)
        },
        // When the layout finishes cooling, park the loop shortly after (unless
        // the pointer is on the graph). If the sim runs long, the bounded
        // keep-alive windows below park it anyway.
        onSimulationStart: () => {
          const dbg = (window as unknown as { __cosmosDbg?: Record<string, number> }).__cosmosDbg
          if (dbg) dbg.simStart = (dbg.simStart ?? 0) + 1
        },
        onSimulationEnd: () => {
          const dbg = (window as unknown as { __cosmosDbg?: Record<string, number> }).__cosmosDbg
          if (dbg) dbg.simEnd = (dbg.simEnd ?? 0) + 1
          armIdlePause(600)
        }
      }

      const instance = new Graph(container, config)
      graphRef.current = instance
      renderingRef.current = true
      // Only gate rendering if cosmos exposes the internal frame controls; on a
      // future rename we leave the loop always-on (original behavior).
      const frameControls = asFrameControls(instance)
      canPauseRef.current =
        typeof frameControls?.startFrames === 'function' &&
        typeof frameControls?.stopFrames === 'function'
      if (typeof window !== 'undefined') {
        ;(window as unknown as { __cosmosDbg?: Record<string, number> }).__cosmosDbg = {
          canPause: canPauseRef.current ? 1 : 0,
          starts: 0,
          stops: 0,
          simStart: 0,
          simEnd: 0
        }
      }

      // Repaint when the container is resized (sidebar open/close, window
      // resize) even while the loop is parked, then fall back to idle.
      const resizeObserver = new ResizeObserver(() => keepAliveFor(600))
      resizeObserver.observe(container)

      return () => {
        resizeObserver.disconnect()
        if (pauseTimerRef.current !== null) clearTimeout(pauseTimerRef.current)
        instance.destroy()
        graphRef.current = null
        dataRef.current = null
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    // Rebuild structural buffers whenever the graph data changes.
    React.useEffect(() => {
      const g = graphRef.current
      if (!g) return

      const hadData = dataRef.current !== null

      // Preserve positions across refetches so the layout stays stable.
      capturePositions()

      const data = toCosmosElements(graph)
      dataRef.current = data

      const positions = buildPointPositions(data, knownPositionsRef.current, SPACE_SIZE)
      g.setPointPositions(positions, true)
      g.setLinks(data.links)
      g.setPointClusters(data.pointClusters)

      const selectedSet = new Set(selectedNodeIds)
      const highlightedSet = new Set(highlightedNodeIds)
      const pointVisuals = buildPointVisuals(data, selectedSet, highlightedSet, hoveredNodeId)
      const linkVisuals = buildLinkVisuals(data, selectedSet, hoveredNodeId)
      g.setPointColors(pointVisuals.colors)
      g.setPointSizes(pointVisuals.sizes)
      g.setLinkColors(linkVisuals.colors)
      g.setLinkWidths(linkVisuals.widths)

      if (!hadData) {
        // Initial load: run the layout normally; cosmos.gl fits on init.
        g.render()
      } else {
        // Refetch: reuse the settled positions and freeze the simulation to
        // avoid churn, then fit once.
        g.render(0)
        requestAnimationFrame(() => graphRef.current?.fitView(400, 0.2))
      }
      // Cover the (re)layout + fit animation, then park the loop. Give a fresh
      // load a longer settle window than a refetch (which reuses positions).
      keepAliveFor(hadData ? 2500 : 6000)
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [graph, capturePositions])

    // Reflect selection / hover / highlight changes via the cheap color buffers.
    React.useEffect(() => {
      applyVisualState()
    }, [applyVisualState])

    // Frame the highlighted nodes when a deep-link highlight arrives.
    const prevHighlightRef = React.useRef<string[]>([])
    React.useEffect(() => {
      const g = graphRef.current
      const data = dataRef.current
      if (!g || !data) return

      const prev = prevHighlightRef.current
      const changed =
        highlightedNodeIds.length !== prev.length ||
        highlightedNodeIds.some((id, i) => id !== prev[i])
      prevHighlightRef.current = highlightedNodeIds
      if (!changed || highlightedNodeIds.length === 0) return

      const indices = highlightedNodeIds
        .map((id) => data.idToIndex.get(id))
        .filter((i): i is number => i !== undefined)
      if (indices.length > 0) {
        g.fitViewByPointIndices(indices, 500, 0.6)
        keepAliveFor(2000)
      }
    }, [highlightedNodeIds, keepAliveFor])

    React.useImperativeHandle(
      ref,
      () => ({
        fit: () => {
          keepAliveFor(1200)
          graphRef.current?.fitView(400, 0.2)
        },
        zoomIn: () => {
          const g = graphRef.current
          if (!g) return
          keepAliveFor(1200)
          g.setZoomLevel(Math.min(10, g.getZoomLevel() * 1.3), 200)
        },
        zoomOut: () => {
          const g = graphRef.current
          if (!g) return
          keepAliveFor(1200)
          g.setZoomLevel(Math.max(0.05, g.getZoomLevel() / 1.3), 200)
        },
        revealNode: (nodeId: string) => {
          const g = graphRef.current
          const index = dataRef.current?.idToIndex.get(nodeId)
          if (g && index !== undefined) {
            keepAliveFor(1200)
            g.zoomToPointByIndex(index, 600, 3, true)
          }
        }
      }),
      [keepAliveFor]
    )

    const handleMouseMove = React.useCallback((event: React.MouseEvent<HTMLDivElement>) => {
      if (!hoveredNodeIdRef.current) return
      const rect = event.currentTarget.getBoundingClientRect()
      setCursorPos({ x: event.clientX - rect.left, y: event.clientY - rect.top })
    }, [])

    return (
      <div
        ref={containerRef}
        onMouseMove={handleMouseMove}
        onMouseEnter={() => {
          // Pointer on the graph → keep the render loop running so hover
          // detection, drag, and wheel-zoom stay live.
          pointerOverRef.current = true
          startRendering()
        }}
        onMouseLeave={() => {
          pointerOverRef.current = false
          setHoveredNodeId(null)
          setCursorPos(null)
          // Pointer left → let the loop park itself shortly after.
          armIdlePause(300)
        }}
        onWheel={() => keepAliveFor(1200)}
        onPointerDown={() => keepAliveFor(1200)}
        style={{ position: 'relative', width: '100%', height }}
      >
        {hoverCluster && cursorPos ? (
          <div
            className="pointer-events-none absolute z-10 max-w-65 truncate rounded-md border border-line bg-bg-elev/95 px-2 py-1 text-xs shadow-md"
            style={{ left: cursorPos.x + 14, top: cursorPos.y + 14 }}
          >
            <span className="font-semibold text-ink-2">{hoverCluster.name}</span>
            <span className="ml-1.5 text-ink-3">{hoverCluster.type}</span>
          </div>
        ) : null}
      </div>
    )
  }
)

export default CosmosGraphCanvas
